import Button from 'components/Button';
import Input from 'components/Input';
import { useCallback, useMemo, useRef, useState } from 'react';
import usePresetStore from 'store/presetStore';
import { IAcceptedObject } from 'types/apiTypes';
import { klona } from 'klona/lite';
import { getProperty, setProperty } from 'dot-prop';
import { z as zod, ZodIssue, ZodType } from 'zod';
import Select from 'components/Select';
import classNames from 'classnames';

// Must check if actorId isn't the current actorId
const formValidation = {
  aduIdentifier: zod.string().nonempty('aduIdentifier is required'),
  senderActorCode: zod.string().nonempty('senderActorCode is required'),
};

const zodSchema = zod.object(formValidation);

const defaultFormData: Partial<IAcceptedObject> = {
  // Fields to just pass on
  aduIdentifier: '',
  senderActorCode: '',
  createdAt: '1997-01-01T00:00:00Z',
  messageType: 'PaymentClaims',
};

interface IAcceptedObjectFormProps {
  formData?: IAcceptedObject;
  loading?: boolean;
  onSubmit: (newData: IAcceptedObject) => void;
  onCancel: () => void;
}

const AcceptedObjectForm = ({
  formData,
  loading = false,
  onSubmit,
  onCancel,
}: IAcceptedObjectFormProps) => {
  const { tollChargerValues } = usePresetStore();
  const [localFormState, setLocalFormState] = useState<Partial<IAcceptedObject>>(
    formData ?? defaultFormData
  );
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const tollChargerValuesOptions = useMemo(() => {
    return [
      { label: '-- Select toll charger --', value: '' },
      ...tollChargerValues.map((tc) => {
        return {
          label: `${tc.label} (${tc.value})`,
          value: tc.value,
        };
      }),
    ];
  }, [tollChargerValues]);
  const senderActorCodeRef = useRef<HTMLSelectElement>(null);

  const onBlurValidation = useCallback(
    (key: string, zodValidation: ZodType) => {
      const value = getProperty(localFormState, key);
      const result = zodValidation.safeParse(value);

      setErrorMessages((prevErrorMessages) => {
        const newErrorMessages: Record<string, string> = { ...prevErrorMessages };
        if (!result.success) {
          // Add error message
          newErrorMessages[key] = result.error.issues[0].message;
        } else {
          // remove error message
          delete newErrorMessages[key];
        }
        return newErrorMessages;
      });
      return result.success;
    },
    [localFormState]
  );

  const onChangeInput = (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const name: string = event.target.name;
    const value: string = event.target.value;
    const newLocalFormState = klona(localFormState);
    setProperty(newLocalFormState, name, value);
    setLocalFormState(newLocalFormState);
  };

  const formatZodErrors = (zodErrors: ZodIssue[]) => {
    const newErrors: Record<string, string> = {};

    zodErrors.forEach((zodError) => {
      const path = zodError.path
        .map((path) => {
          if (typeof path === 'number') {
            return `[${path}]`;
          }
          return path;
        })
        .join('.')
        .replace(/\.\[/g, '[');

      newErrors[path] = zodError.message;
    });
    setErrorMessages(newErrors);
  };

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Validate form
      const safeParsedZod = zodSchema.safeParse(localFormState);

      if (!safeParsedZod.success) {
        formatZodErrors(safeParsedZod.error.issues);
      }

      if (safeParsedZod.success && onSubmit) {
        onSubmit(localFormState as IAcceptedObject);
      }
    },
    [localFormState, onSubmit]
  );

  return (
    <form onSubmit={onSubmitForm} className="min-w-[320px] max-w-[800px]">
      <div className="grid grid-cols-1 gap-4 px-2">
        <Input
          autoComplete="off"
          label="aduIdentifier"
          name="aduIdentifier"
          value={localFormState.aduIdentifier ?? ''}
          onChange={onChangeInput}
          error={errorMessages['aduIdentifier']}
          onBlur={() => onBlurValidation('aduIdentifier', formValidation.aduIdentifier)}
          disabled={loading}
        />

        <Select
          inputClassName={classNames(!localFormState.senderActorCode && 'text-neutral-500')}
          value={localFormState.senderActorCode ?? ''}
          label="senderActorCode"
          name="senderActorCode"
          options={tollChargerValuesOptions}
          ref={senderActorCodeRef}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            onChangeInput(event);
            setTimeout(() => {
              senderActorCodeRef.current?.blur();
            }, 0);
          }}
          error={errorMessages['senderActorCode']}
          onBlur={() => onBlurValidation('senderActorCode', formValidation.senderActorCode)}
          disabled={loading}
        />
      </div>

      <div className="flex justify-between pt-8">
        <Button variant="ghost" type="button" disabled={loading} size="sm" onClick={onCancel}>
          Cancel
        </Button>

        <Button variant="primary" type="submit" spinner={loading} size="sm">
          Save
        </Button>
      </div>
    </form>
  );
};

export default AcceptedObjectForm;
